import type { JSONObject } from "../Support";
import type { LocationSettings } from "../location-common";

// FIXME: This `any` should be `unknown`
export interface DocumentData {
    [field: string]: any;
}
export type AsDocumentData<T> = { readonly [P in keyof T]: any };

export type QueryOpString = "==" | "<" | "<=" | ">=" | ">" | "in" | "array-contains" | "array-contains-any";

export interface Query {
    readonly fieldPath: string;
    readonly opString: QueryOpString;
    readonly value: unknown;
}

export interface DocumentDataWithID {
    readonly data: DocumentData;
    readonly id: string;
}

export interface DocumentDataWithIDAndPath extends DocumentDataWithID {
    readonly path: string;
}

export type QueryResults = readonly DocumentDataWithIDAndPath[];

export type SortDirection = "asc" | "desc";

export interface DataReader {
    readonly database: Database;
    getDocument(collectionName: string, id: string): Promise<DocumentData | undefined>;
}

export interface QueryableDataReader extends DataReader {
    getDocumentsWhere(
        collectionName: string,
        queries: readonly Query[],
        sortFieldPath?: string,
        sortDirection?: SortDirection,
        limit?: number
    ): Promise<QueryResults>;
}

export interface DataWriter {
    readonly database: Database;
    setDocument(collectionName: string, id: string | undefined, data: DocumentData): Promise<string>;
    updateDocument(collectionName: string, id: string | undefined, updates: DocumentData): Promise<string>;
    updateDocumentWithNesting(collectionName: string, id: string, data: DocumentData): Promise<void>;
    deleteDocument(collectionName: string, id: string): Promise<void>;
}

export interface DataReaderWriter extends DataReader, DataWriter {}

export interface Batch extends DataWriter {}

export interface Transaction extends DataReaderWriter {}

export interface DiffResult extends DocumentDataWithID {
    readonly kind: "added" | "modified" | "removed";
}

export type DiffResults = readonly DiffResult[];

export abstract class Database implements DataReaderWriter {
    public get deleteFieldValue(): unknown {
        throw new Error();
    }
    public get serverTimestampFieldValue(): unknown {
        throw new Error();
    }
    public abstract get deploymentLocationSettings(): LocationSettings;
    public abstract arrayUnionFieldValue(...values: unknown[]): unknown;
    public abstract arrayRemoveFieldValue(...values: unknown[]): unknown;
    public abstract incrementFieldValue(increment?: number): unknown;

    public get database(): Database {
        return this;
    }

    public abstract get projectID(): string;

    public abstract makeDocumentID(): Promise<string>;

    public abstract getDocument(collectionName: string, id: string): Promise<DocumentData | undefined>;
    public abstract getDocumentsWhere(
        collectionName: string,
        queries: ReadonlyArray<Query>,
        sortFieldPath?: string,
        sortDirection?: SortDirection,
        limit?: number
    ): Promise<QueryResults>;
    public abstract getDocumentsWherePaginated(
        collectionName: string,
        queries: ReadonlyArray<Query>,
        pageSize: number,
        concurrently: boolean,
        callback: (page: QueryResults) => Promise<unknown>
    ): Promise<void>;

    public abstract setDocument(collectionName: string, id: string | undefined, data: DocumentData): Promise<string>;
    public abstract updateDocument(
        collectionName: string,
        id: string | undefined,
        updates: DocumentData
    ): Promise<string>;
    public abstract updateDocumentWithNesting(collectionName: string, id: string, data: DocumentData): Promise<void>;
    public abstract deleteDocument(collectionName: string, id: string): Promise<void>;

    public abstract runBatch<T>(name: string, f: (b: Batch) => Promise<T>): Promise<T>;
    public abstract runTransaction<T>(name: string, f: (t: Transaction) => Promise<T>): Promise<T>;

    public abstract listenToDocument(
        collectionName: string,
        id: string,
        onUpdate: (data: DocumentData) => void,
        onError?: (e: Error) => void
    ): () => void;
    public abstract listenWhere(
        collectionName: string,
        queries: readonly Query[],
        sortFieldPath: string | undefined,
        sortDirection: SortDirection | undefined,
        limit: number | undefined,
        onUpdate: (results: QueryResults) => void,
        onError?: (e: Error) => void
    ): () => void;
    public abstract listenDiffWhere(
        collectionName: string,
        queries: readonly Query[],
        sortFieldPath: string | undefined,
        sortDirection: SortDirection | undefined,
        onUpdate: (results: DiffResults) => void,
        onError: () => void
    ): () => void;

    public abstract dateFromTimestamp(timestamp: unknown): Date;
    public abstract convertDocumentProperty(x: unknown): unknown;

    public abstract convertFromDocument(data: DocumentData): JSONObject;
    public abstract convertToDocument(data: DocumentData): DocumentData;

    public abstract enterNetworkConnectionLock(): Promise<void>;
    public abstract exitNetworkConnectionLock(): void;

    public abstract isAvailable(): boolean;
}
